import { Suspense, lazy, useState } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { TripsCard } from './TripsCard.js';
import {
  type ChangeTone,
  averageRental,
  changeLabel,
  chartSeries,
  chartSummary,
  monthName,
  ringgitCents,
} from './dashboardSeries.js';
import type { MonthSummary, OverviewMoney } from './types.js';

/**
 * 422 (#1526, design approved 2026-10-27): the SuperAdmin's month. Every money figure is the month's (booked ahead
 * included, as the Sales Statement counts it); "so far" and the change against the same stretch of last month sit
 * under it. Total revenue = rental revenue + platform fee + penalties, drawn as one split bar. The revenue by car,
 * campus and supplier adds up to the rental revenue.
 */
const DailyRevenueChart = lazy(() => import('./DailyRevenueChart.js'));

const TONE: Record<ChangeTone, string> = {
  up: 'text-semantic-success-text',
  down: 'text-semantic-danger-text',
  flat: 'text-steel dark:text-slate',
  new: 'text-primary',
};

function Change({ current, previous }: { current: number; previous: number }) {
  const c = changeLabel(current, previous);
  return <b className={`font-semibold ${TONE[c.tone]}`}>{c.text}</b>;
}

/** Occupancy moves in percentage points, not percent of itself. */
function PointsChange({ current, previous }: { current: number | null; previous: number | null }) {
  if (current === null || previous === null) return null;
  const pts = Math.round(current * 100) - Math.round(previous * 100);
  const tone: ChangeTone = pts > 0 ? 'up' : pts < 0 ? 'down' : 'flat';
  const text = pts > 0 ? `▲ ${pts} pts` : pts < 0 ? `▼ ${Math.abs(pts)} pts` : '0 pts';
  return <b className={`font-semibold ${TONE[tone]}`}>{text}</b>;
}

const percent = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);

/** 422 v2: cards stand off the page in dark mode (surface fill, hairline-strong edge). */
const RAISED = 'dark:border-hairline-strong dark:bg-surface';
/** 422 v2: links and muted text measured to WCAG AAA (7:1) in dark mode. */
const LINK = 'text-primary hover:underline dark:text-brand-blue-mid';
const MUTED = 'text-steel dark:text-slate';

function Tile({
  id,
  label,
  value,
  accent,
  children,
}: {
  id: string;
  label: string;
  value: string;
  accent?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-testid={`overview-${id}`}
      className={`flex min-w-0 flex-col gap-1 rounded-xl border p-4 ${
        accent
          ? 'border-brand-blue-soft bg-surface-feature dark:border-primary/35'
          : `border-hairline bg-canvas ${RAISED}`
      }`}
    >
      <span className={`text-xs font-semibold text-slate ${accent ? 'dark:text-charcoal' : ''}`}>
        {label}
      </span>
      <span className="text-[22px] font-bold whitespace-nowrap text-ink tabular-nums">{value}</span>
      {children ? (
        <span
          className={`flex flex-col gap-px text-xs ${accent ? 'text-slate dark:text-charcoal' : MUTED}`}
        >
          {children}
        </span>
      ) : null}
    </div>
  );
}

const SPLIT = [
  { key: 'rental', label: 'Rental revenue (no platform fee)', bar: 'bg-primary' },
  { key: 'fee', label: 'Platform fee', bar: 'bg-brand-yellow' },
  { key: 'penalty', label: 'Penalties', bar: 'bg-brand-navy' },
] as const;

type ByTab = 'car' | 'campus' | 'supplier';

function monthDays(label: string): { from: string; to: string } {
  const [y, m] = label.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${label}-01`, to: `${label}-${String(last).padStart(2, '0')}` };
}

export function OverviewMonth({
  data,
  overview,
  today,
}: {
  data: MonthSummary;
  overview: OverviewMoney;
  today: string;
}) {
  const [tab, setTab] = useState<ByTab>('car');
  const o = overview;
  const name = monthName(data.month).split(' ')[0] ?? data.month;
  const comparedWith = `1–${data.day_of_month} of last month`;
  const empty = data.revenue_minor === 0;
  const parts = { rental: data.revenue_minor, fee: o.platform_fee_minor, penalty: o.penalty_minor };
  const share = (v: number) => (o.total_revenue_minor > 0 ? (v / o.total_revenue_minor) * 100 : 0);
  const days = monthDays(data.month);
  const penaltiesHref = `/penalties?from=${days.from}&to=${days.to}&status=active`;
  const rentalCommission = o.commission_minor - o.penalty_commission_minor;

  const rows =
    tab === 'car'
      ? data.top_cars.map((t) => ({
          key: t.car_id,
          name: t.plate ?? '—',
          sub: t.model,
          rentals: null as number | null,
          revenue: t.revenue_minor,
        }))
      : (tab === 'campus' ? (data.by_campus ?? []) : (data.by_supplier ?? [])).map((r) => ({
          key: 'location_id' in r ? r.location_id || 'none' : r.supplier_id || 'none',
          name: r.name,
          sub: '',
          rentals: r.rentals as number | null,
          revenue: r.revenue_minor,
        }));

  return (
    <div className="flex min-w-0 flex-col gap-4" data-testid="overview-month">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[13px] font-bold tracking-wider text-ink uppercase">{name}</h2>
        <span className={`text-xs ${MUTED}`}>
          1–{data.day_of_month} {name.slice(0, 3)}, against {comparedWith} · paid bookings by pickup
          date, as in the Sales Statement
        </span>
      </div>

      <Card data-testid="overview-total" className={RAISED}>
        {/* 422 v2 (design frame 1): the total full width, its split beside it. */}
        <div className="grid items-center gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="flex flex-col gap-1.5">
            <span className={`text-sm font-semibold ${MUTED}`}>
              Total revenue (with platform fee and penalties)
            </span>
            <span className="text-[32px] font-bold text-ink tabular-nums sm:text-[36px]">
              {ringgitCents(o.total_revenue_minor)}
            </span>
            <span className={`text-xs ${MUTED}`}>
              {ringgitCents(o.total_revenue_so_far_minor)} so far{' '}
              <Change
                current={o.total_revenue_so_far_minor}
                previous={o.total_revenue_prev_so_far_minor}
              />{' '}
              vs {ringgitCents(o.total_revenue_prev_so_far_minor)} in {comparedWith}
            </span>
          </div>
          <div>
            <div
              className="mb-2.5 flex h-3 overflow-hidden rounded-md bg-hairline-soft"
              aria-hidden="true"
            >
              {SPLIT.map((sp) => (
                <div
                  key={sp.key}
                  data-testid={`overview-split-${sp.key}`}
                  className={sp.bar}
                  style={{ width: `${share(parts[sp.key])}%` }}
                />
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-0.5" data-testid="overview-rental">
                <span className="flex items-center gap-1.5 text-xs text-slate">
                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
                  {SPLIT[0].label}
                </span>
                <b className="text-[17px] text-ink tabular-nums">
                  {ringgitCents(data.revenue_minor)}
                </b>
                <span className={`text-xs ${MUTED}`}>
                  <Change
                    current={data.revenue_so_far_minor}
                    previous={data.revenue_prev_so_far_minor}
                  />{' '}
                  · {data.rentals} {data.rentals === 1 ? 'rental' : 'rentals'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5" data-testid="overview-fee">
                <span className="flex items-center gap-1.5 text-xs text-slate">
                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-brand-yellow" />
                  {SPLIT[1].label}
                </span>
                <b className="text-[17px] text-ink tabular-nums">
                  {ringgitCents(o.platform_fee_minor)}
                </b>
                <span className={`text-xs ${MUTED}`}>
                  {o.fee_legs} booking {o.fee_legs === 1 ? 'leg' : 'legs'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5" data-testid="overview-penalty">
                <span className="flex items-center gap-1.5 text-xs text-slate">
                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-brand-navy" />
                  {SPLIT[2].label}
                </span>
                <Link to={penaltiesHref} className={`text-[17px] font-bold tabular-nums ${LINK}`}>
                  {ringgitCents(o.penalty_minor)}
                </Link>
                <span className={`text-xs ${MUTED}`}>
                  {o.penalties} {o.penalties === 1 ? 'penalty' : 'penalties'} · paid or not
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile id="rentals" label="Rentals" value={String(data.rentals)}>
          <span>
            {data.rentals_so_far} so far{' '}
            <Change current={data.rentals_so_far} previous={data.rentals_prev_so_far} />
          </span>
        </Tile>
        <Tile
          id="average"
          label="Average rental"
          value={averageRental(data.revenue_minor, data.rentals)}
        >
          <span>
            {averageRental(data.revenue_so_far_minor, data.rentals_so_far)} so far vs{' '}
            {averageRental(data.revenue_prev_so_far_minor, data.rentals_prev_so_far)}
          </span>
        </Tile>
        <Tile id="occupancy" label="Fleet occupancy" value={percent(data.fleet_busy)}>
          <span>
            {percent(o.fleet_busy_so_far)} so far{' '}
            <PointsChange current={o.fleet_busy_so_far} previous={o.fleet_busy_prev_so_far} /> ·{' '}
            {data.fleet_cars} {data.fleet_cars === 1 ? 'car' : 'cars'}
          </span>
        </Tile>
        <Tile id="users" label="Active users" value={String(o.active_users)}>
          <span>
            {o.active_users_so_far} so far{' '}
            <Change current={o.active_users_so_far} previous={o.active_users_prev_so_far} /> ·
            customers who rented
          </span>
        </Tile>
        <Tile
          id="commission"
          label="CARii commission"
          value={ringgitCents(o.commission_minor)}
          accent
        >
          <span>
            rentals {ringgitCents(rentalCommission)} + penalties{' '}
            {ringgitCents(o.penalty_commission_minor)}
          </span>
        </Tile>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card padding="none" className={`px-4 pt-4 pb-3 ${RAISED}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-ink">Rental revenue by day</span>
          </div>
          <p data-testid="chart-summary" className="sr-only">
            {chartSummary(data.daily)}
          </p>
          {empty ? (
            <p data-testid="month-empty" className={`py-10 text-center text-sm ${MUTED}`}>
              No paid rentals yet this month.
            </p>
          ) : (
            <div className="mt-3 h-[180px]" aria-hidden="true">
              <Suspense fallback={<div className="h-[180px]" />}>
                <DailyRevenueChart points={chartSeries(data.daily, today)} />
              </Suspense>
            </div>
          )}
        </Card>
        <Card padding="none" className={`px-4 pt-4 pb-3 ${RAISED}`} data-testid="overview-by">
          <span className="text-sm font-semibold text-ink">Rental revenue by</span>
          <div
            className="mt-3 mb-2 flex flex-wrap gap-1.5"
            role="tablist"
            aria-label="Rental revenue by"
          >
            {(
              [
                ['car', 'Car'],
                ['campus', 'Campus'],
                ['supplier', 'Supplier'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  tab === key
                    ? 'border-primary bg-primary font-bold text-on-primary'
                    : 'border-hairline-strong bg-canvas text-slate dark:bg-surface'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {rows.length === 0 ? (
            <p className={`py-6 text-center text-sm ${MUTED}`}>No paid rentals yet this month.</p>
          ) : (
            rows.map((r) => (
              <div
                key={r.key}
                data-testid="overview-by-row"
                className="flex flex-col gap-1.5 border-t border-hairline-soft py-2.5 text-sm"
              >
                <div className="flex justify-between gap-2">
                  <span className="min-w-0 truncate">
                    <b className="font-semibold text-ink">{r.name}</b>
                    {r.sub ? <span className="ml-1.5 text-slate">{r.sub}</span> : null}
                  </span>
                  <span className="whitespace-nowrap text-ink tabular-nums">
                    {ringgitCents(r.revenue)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-hairline-soft" aria-hidden="true">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{
                      width: `${data.revenue_minor > 0 ? (r.revenue / data.revenue_minor) * 100 : 0}%`,
                    }}
                  />
                </div>
                {r.rentals !== null ? (
                  <span className={`text-xs ${MUTED}`}>
                    {r.rentals} {r.rentals === 1 ? 'rental' : 'rentals'}
                  </span>
                ) : null}
              </div>
            ))
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-2.5">
            {tab === 'car' ? (
              <Link to="/revenue-by-car" className={`text-sm font-medium ${LINK}`}>
                Revenue by Car →
              </Link>
            ) : null}
            <Link to="/sales-statement" className={`text-sm font-medium ${LINK}`}>
              Sales Statement →
            </Link>
          </div>
        </Card>
      </div>

      {data.trips ? <TripsCard trips={data.trips} month={data.month} wide /> : null}
    </div>
  );
}
